/**
 * AntecedentesTab — Patrón master-detail para antecedentes del expediente.
 * Sidebar izquierda con 4 secciones + panel derecho con formulario.
 * Auto-guardado con debounce 1.5s.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, FormControl, FormControlLabel, InputLabel, Select, MenuItem,
  Checkbox, Switch, Button, Chip, IconButton, CircularProgress, LinearProgress, Alert,
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  MedicalServices as MedicalIcon,
  FitnessCenter as FitnessIcon,
  PregnantWoman as PregnantIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const CONDICIONES_FAMILIARES = [
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'hipertension', label: 'Hipertensión' },
  { id: 'cancer', label: 'Cáncer' },
  { id: 'cardiopatia', label: 'Cardiopatía' },
  { id: 'enfermedad_renal', label: 'Enfermedad renal' },
  { id: 'neurologica', label: 'Neurológica' },
  { id: 'psiquiatrica', label: 'Psiquiátrica' },
  { id: 'asma', label: 'Asma' },
  { id: 'obesidad', label: 'Obesidad' },
  { id: 'hepatica', label: 'Hepática' },
];

const FAMILIARES = [
  { id: 'padre', label: 'Padre' },
  { id: 'madre', label: 'Madre' },
  { id: 'abuelos_paternos', label: 'Abuelos paternos' },
  { id: 'abuelos_maternos', label: 'Abuelos maternos' },
  { id: 'tios_tias', label: 'Tíos / Tías' },
];

const SECCIONES_ANT = [
  { key: 'heredo', label: 'Heredo-familiares', icono: <FamilyIcon />, color: '#a855f7' },
  { key: 'patologicos', label: 'Patológicos', icono: <MedicalIcon />, color: '#0ea5e9' },
  { key: 'noPatologicos', label: 'No patológicos', icono: <FitnessIcon />, color: '#10b981' },
  { key: 'gineco', label: 'Gineco-obstétricos', icono: <PregnantIcon />, color: '#ec4899', soloMujer: true },
];

export default function AntecedentesTab({ expedienteId, paciente, userId, onAntecedentesActualizados }) {
  const esMujer = (paciente?.sexo || '').toLowerCase() === 'femenino';
  const [seccionActiva, setSeccionActiva] = useState('heredo');

  const [heredo, setHeredo] = useState({});
  const [patologicos, setPatologicos] = useState({});
  const [noPatologicos, setNoPatologicos] = useState({});
  const [gineco, setGineco] = useState({});

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardadoHace, setGuardadoHace] = useState(null);
  const [error, setError] = useState('');
  const initialLoadDone = useRef(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (!expedienteId) return;
    (async () => {
      setCargando(true);
      try {
        const res = await fetch(`${API_URL}/emr/expedientes/${expedienteId}`, { headers: { 'X-User-Id': userId } });
        if (!res.ok) throw new Error('No se pudo cargar el expediente');
        const data = await res.json();
        setHeredo(data.antecedentes_heredo_familiares || {});
        setPatologicos(data.antecedentes_personales_patologicos || {});
        setNoPatologicos(data.antecedentes_personales_no_patologicos || {});
        setGineco(data.antecedentes_gineco_obstetricos || {});
        initialLoadDone.current = true;
      } catch (e) { setError(e.message); } finally { setCargando(false); }
    })();
  }, [expedienteId, userId]);

  const guardar = useCallback(async (silencioso = false) => {
    if (!expedienteId || !initialLoadDone.current) return;
    if (!silencioso) setGuardando(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/emr/expedientes/${expedienteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          antecedentes_heredo_familiares: heredo,
          antecedentes_personales_patologicos: patologicos,
          antecedentes_personales_no_patologicos: noPatologicos,
          antecedentes_gineco_obstetricos: gineco,
        }),
      });
      if (!res.ok) throw new Error('Error al guardar');
      setGuardadoHace(Date.now());
      // Notificar al padre para que re-fetchee el expediente y, p.ej., el banner
      // "Falta capturar antecedentes" desaparezca cuando ya hay datos.
      onAntecedentesActualizados?.();
    } catch (e) { setError(e.message); } finally { setGuardando(false); }
  }, [expedienteId, heredo, patologicos, noPatologicos, gineco, userId, onAntecedentesActualizados]);

  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => guardar(true), 1500);
    return () => clearTimeout(debounceTimer.current);
  }, [heredo, patologicos, noPatologicos, gineco]);

  const tieneCondicion = (familiar, condicion) => (heredo[familiar] || []).includes(condicion);
  const toggleCondicionFamiliar = (familiar, condicion) => {
    setHeredo(prev => {
      const actuales = prev[familiar] || [];
      return { ...prev, [familiar]: actuales.includes(condicion) ? actuales.filter(c => c !== condicion) : [...actuales, condicion] };
    });
  };

  const agregarItem = (campo, item) => setPatologicos(prev => ({ ...prev, [campo]: [...(prev[campo] || []), item] }));
  const eliminarItem = (campo, idx) => setPatologicos(prev => ({ ...prev, [campo]: (prev[campo] || []).filter((_, i) => i !== idx) }));

  const completitud = {
    heredo: Object.keys(heredo).some(f => (heredo[f] || []).length > 0) || heredo.otros,
    patologicos: (patologicos.enfermedades_cronicas?.length > 0) || (patologicos.alergias?.length > 0) || (patologicos.cirugias?.length > 0) || patologicos.trauma,
    noPatologicos: noPatologicos.tabaquismo_estado || noPatologicos.alcoholismo_estado || noPatologicos.actividad_fisica || noPatologicos.dieta,
    gineco: esMujer && (gineco.menarca || gineco.gestaciones !== undefined),
  };

  const secciones = SECCIONES_ANT.filter(s => !s.soloMujer || esMujer);
  const totalSecciones = secciones.length;
  const seccionesLlenas = secciones.filter(s => completitud[s.key]).length;
  const porcentaje = Math.round((seccionesLlenas / totalSecciones) * 100);

  if (cargando) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Barra de progreso + estado guardado */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {seccionesLlenas} de {totalSecciones} secciones
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {guardando ? (
            <Chip icon={<SyncIcon sx={{ fontSize: 13 }} />} label="Guardando..." size="small" sx={{ height: 22, fontSize: 10, background: '#fef3c7', color: '#92400e' }} />
          ) : guardadoHace ? (
            <Chip icon={<CheckIcon sx={{ fontSize: 13 }} />} label={`Guardado ${hace(guardadoHace)}`} size="small" sx={{ height: 22, fontSize: 10, background: '#d1fae5', color: '#065f46' }} />
          ) : null}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: porcentaje === 100 ? '#10b981' : '#64748b' }}>{porcentaje}%</Typography>
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={porcentaje} sx={{ height: 4, borderRadius: 2, mb: 1.5, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: porcentaje === 100 ? 'linear-gradient(90deg, #10b981, #059669)' : 'linear-gradient(90deg, #8b5cf6, #6366f1)', borderRadius: 2 } }} />

      {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}

      {/* Layout master-detail */}
      <Box sx={{ display: 'flex', gap: 0, minHeight: 420, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>

        {/* Sidebar */}
        <Box sx={{ width: { xs: 48, sm: 180 }, flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
          {secciones.map(s => {
            const activa = seccionActiva === s.key;
            const llena = completitud[s.key];
            return (
              <Box key={s.key} onClick={() => setSeccionActiva(s.key)} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: { xs: 0.8, sm: 1.5 }, py: 1.8,
                cursor: 'pointer', position: 'relative', transition: 'all 0.2s',
                background: activa ? '#fff' : 'transparent', borderBottom: '1px solid #f1f5f9',
                '&:hover': { background: activa ? '#fff' : '#f1f5f9' },
                '&::before': activa ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, borderRadius: '0 3px 3px 0', background: s.color } : {},
              }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: 2, flexShrink: 0,
                  background: activa ? s.color : llena ? `${s.color}20` : '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: activa ? `0 3px 10px ${s.color}40` : 'none', transition: 'all 0.2s',
                }}>
                  {React.cloneElement(s.icono, { sx: { fontSize: 16, color: activa ? '#fff' : llena ? s.color : '#94a3b8' } })}
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: activa ? 700 : 500, color: activa ? '#1e293b' : '#64748b' }} noWrap>{s.label}</Typography>
                  {llena && <CheckIcon sx={{ fontSize: 13, color: '#10b981', flexShrink: 0 }} />}
                </Box>
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
          <Box sx={{ p: 1, borderTop: '1px solid #e2e8f0' }}>
            <Button size="small" fullWidth startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
              onClick={() => guardar(false)} disabled={guardando}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: 0, sm: 10 }, color: '#10b981', borderRadius: 2, border: '1px solid #10b981', minWidth: 0, px: { xs: 0.5, sm: 1 }, '&:hover': { background: '#ecfdf5' } }}>
              <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Guardar</Box>
            </Button>
          </Box>
        </Box>

        {/* Panel derecho */}
        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, overflowY: 'auto', maxHeight: 500 }}>

          {/* Heredo-familiares */}
          {seccionActiva === 'heredo' && (
            <Box>
              <SeccionHeader icono={<FamilyIcon />} color="#a855f7" titulo="Heredo-familiares" subtitulo="Antecedentes patológicos de familiares directos" />
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { padding: '6px 8px', textAlign: 'center', fontSize: 12 }, '& th': { background: '#f1f5f9', color: '#1e3a5f', fontWeight: 700, fontSize: 10 }, '& tr:nth-of-type(even)': { background: '#fafafa' } }}>
                  <thead><tr><th style={{ textAlign: 'left', minWidth: 120 }}>Familiar</th>{CONDICIONES_FAMILIARES.map(c => <th key={c.id}>{c.label}</th>)}</tr></thead>
                  <tbody>{FAMILIARES.map(f => <tr key={f.id}><td style={{ textAlign: 'left', fontWeight: 600, fontSize: 12 }}>{f.label}</td>{CONDICIONES_FAMILIARES.map(c => <td key={c.id}><Checkbox size="small" checked={tieneCondicion(f.id, c.id)} onChange={() => toggleCondicionFamiliar(f.id, c.id)} sx={{ p: 0.3 }} /></td>)}</tr>)}</tbody>
                </Box>
              </Box>
              <TextField fullWidth label="Otros antecedentes heredo-familiares" multiline minRows={2} value={heredo.otros || ''} onChange={(e) => setHeredo({ ...heredo, otros: e.target.value })} InputLabelProps={{ shrink: true }} size="small" placeholder="Ej: Tío materno con Alzheimer, prima con lupus" />
            </Box>
          )}

          {/* Patológicos */}
          {seccionActiva === 'patologicos' && (
            <Box>
              <SeccionHeader icono={<MedicalIcon />} color="#0ea5e9" titulo="Personales patológicos" subtitulo="Enfermedades, cirugías, alergias y hospitalizaciones previas" />
              <Grid container spacing={2}>
                <Grid item xs={12}><ListaDinamica titulo="Enfermedades crónicas" placeholder="Ej: Diabetes tipo 2, Hipertensión" items={patologicos.enfermedades_cronicas || []} onAdd={(v) => agregarItem('enfermedades_cronicas', { nombre: v })} onRemove={(i) => eliminarItem('enfermedades_cronicas', i)} renderItem={(item) => item.nombre} /></Grid>
                <Grid item xs={12}><ListaDinamica titulo="Alergias conocidas" placeholder="Ej: Penicilina, Mariscos" items={patologicos.alergias || []} onAdd={(v) => agregarItem('alergias', { sustancia: v })} onRemove={(i) => eliminarItem('alergias', i)} renderItem={(item) => item.sustancia + (item.reaccion ? ` (${item.reaccion})` : '')} colorChip="#fee2e2" colorTexto="#991b1b" /></Grid>
                <Grid item xs={12}><ListaDinamica titulo="Cirugías previas" placeholder="Ej: Apendicectomía 2015" items={patologicos.cirugias || []} onAdd={(v) => agregarItem('cirugias', { nombre: v })} onRemove={(i) => eliminarItem('cirugias', i)} renderItem={(item) => item.nombre} /></Grid>
                <Grid item xs={12}><ListaDinamica titulo="Hospitalizaciones previas" placeholder="Ej: Neumonía 2022" items={patologicos.hospitalizaciones || []} onAdd={(v) => agregarItem('hospitalizaciones', { motivo: v })} onRemove={(i) => eliminarItem('hospitalizaciones', i)} renderItem={(item) => item.motivo} /></Grid>
                <Grid item xs={12} sm={6}><FormControlLabel control={<Switch checked={!!patologicos.transfusiones?.tuvo} onChange={(e) => setPatologicos({ ...patologicos, transfusiones: { ...(patologicos.transfusiones || {}), tuvo: e.target.checked } })} />} label="Transfusiones sanguíneas previas" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Trauma significativo" multiline minRows={2} value={patologicos.trauma || ''} onChange={(e) => setPatologicos({ ...patologicos, trauma: e.target.value })} InputLabelProps={{ shrink: true }} size="small" placeholder="Fracturas, accidentes graves" /></Grid>
              </Grid>
            </Box>
          )}

          {/* No patológicos */}
          {seccionActiva === 'noPatologicos' && (
            <Box>
              <SeccionHeader icono={<FitnessIcon />} color="#10b981" titulo="Personales no patológicos" subtitulo="Estilo de vida, hábitos y vivienda" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Tabaquismo</InputLabel><Select notched label="Tabaquismo" displayEmpty value={noPatologicos.tabaquismo_estado || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, tabaquismo_estado: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="no">No fuma</MenuItem><MenuItem value="activo">Fumador activo</MenuItem><MenuItem value="suspendido">Suspendido (exfumador)</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Paquetes-año" type="number" value={noPatologicos.tabaquismo_paquetes_anio || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, tabaquismo_paquetes_anio: e.target.value })} disabled={noPatologicos.tabaquismo_estado === 'no' || !noPatologicos.tabaquismo_estado} InputLabelProps={{ shrink: true }} size="small" placeholder="Paquetes diarios × años" /></Grid>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Alcoholismo</InputLabel><Select notched label="Alcoholismo" displayEmpty value={noPatologicos.alcoholismo_estado || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, alcoholismo_estado: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="no">No consume</MenuItem><MenuItem value="social">Social / ocasional</MenuItem><MenuItem value="frecuente">Frecuente</MenuItem><MenuItem value="problematico">Problemático</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Consumo de drogas</InputLabel><Select notched label="Consumo de drogas" displayEmpty value={noPatologicos.drogas || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, drogas: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="no">No</MenuItem><MenuItem value="si">Sí</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Actividad física</InputLabel><Select notched label="Actividad física" displayEmpty value={noPatologicos.actividad_fisica || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, actividad_fisica: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="sedentario">Sedentario</MenuItem><MenuItem value="moderado">Moderado</MenuItem><MenuItem value="intenso">Intenso</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Dieta</InputLabel><Select notched label="Dieta" displayEmpty value={noPatologicos.dieta || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, dieta: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="equilibrada">Equilibrada</MenuItem><MenuItem value="pobre">Pobre / desbalanceada</MenuItem><MenuItem value="vegetariana">Vegetariana</MenuItem><MenuItem value="vegana">Vegana</MenuItem><MenuItem value="especial">Con restricciones</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><FormControl fullWidth size="small"><InputLabel shrink>Inmunizaciones</InputLabel><Select notched label="Inmunizaciones" displayEmpty value={noPatologicos.inmunizaciones || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, inmunizaciones: e.target.value })}><MenuItem value=""><em>No especificado</em></MenuItem><MenuItem value="completas">Completas</MenuItem><MenuItem value="incompletas">Incompletas</MenuItem><MenuItem value="desconocido">Desconocido</MenuItem></Select></FormControl></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Horas de sueño" type="number" value={noPatologicos.sueno_horas || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, sueno_horas: e.target.value })} InputLabelProps={{ shrink: true }} size="small" placeholder="Ej: 7" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Vivienda / observaciones" multiline minRows={2} value={noPatologicos.vivienda_obs || ''} onChange={(e) => setNoPatologicos({ ...noPatologicos, vivienda_obs: e.target.value })} InputLabelProps={{ shrink: true }} size="small" placeholder="Propia/arrendada, hacinamiento, mascotas, fauna nociva" /></Grid>
              </Grid>
            </Box>
          )}

          {/* Gineco-obstétricos */}
          {seccionActiva === 'gineco' && esMujer && (
            <Box>
              <SeccionHeader icono={<PregnantIcon />} color="#ec4899" titulo="Gineco-obstétricos" subtitulo="Historia menstrual, obstétrica y preventiva" />

              {/* Bloque 1: Historia menstrual */}
              <Box sx={{ p: 2, borderRadius: 2.5, background: '#fdf2f8', border: '1px solid #fbcfe8', mb: 2 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9d174d', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
                  Historia menstrual
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>Menarca</Typography>
                    <TextField type="number" size="small" value={gineco.menarca ?? ''} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setGineco({ ...gineco, menarca: v === '' ? '' : Number(v) }); }}
                      inputProps={{ min: 0, max: 25, inputMode: 'numeric', style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 4px' } }}
                      InputProps={{ endAdornment: <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>años</Typography> }}
                      sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>FUM</Typography>
                    <TextField type="date" size="small" value={gineco.fum || ''} onChange={(e) => setGineco({ ...gineco, fum: e.target.value })} InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: 13, padding: '6px 8px' } }}
                      sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>Climaterio</Typography>
                    <TextField type="number" size="small" value={gineco.climaterio ?? ''} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setGineco({ ...gineco, climaterio: v === '' ? '' : Number(v) }); }}
                      inputProps={{ min: 0, max: 80, inputMode: 'numeric', style: { textAlign: 'center', fontWeight: 600, fontSize: 14, padding: '6px 4px' } }}
                      InputProps={{ endAdornment: <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>años</Typography> }}
                      placeholder="—"
                      sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                    />
                    <Typography sx={{ fontSize: 9, color: '#94a3b8', mt: 0.3 }}>Vacío si aún menstrua</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Bloque 2: Historia obstétrica */}
              <Box sx={{ p: 2, borderRadius: 2.5, background: '#fdf2f8', border: '1px solid #fbcfe8', mb: 2 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9d174d', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
                  Historia obstétrica
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                  {[
                    { key: 'gestaciones', label: 'Gestaciones', abbr: 'G' },
                    { key: 'partos', label: 'Partos', abbr: 'P' },
                    { key: 'cesareas', label: 'Cesáreas', abbr: 'C' },
                    { key: 'abortos', label: 'Abortos', abbr: 'A' },
                    { key: 'obitos', label: 'Óbitos', abbr: 'O' },
                    { key: 'ectopicos', label: 'Ectópicos', abbr: 'E' },
                  ].map(campo => (
                    <Box key={campo.key} sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9d174d', mb: 0.3 }}>{campo.abbr} · {campo.label}</Typography>
                      <TextField type="number" size="small" value={gineco[campo.key] ?? ''} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setGineco({ ...gineco, [campo.key]: v === '' ? '' : Number(v) }); }}
                        inputProps={{ min: 0, max: 30, inputMode: 'numeric', style: { textAlign: 'center', fontWeight: 700, fontSize: 16, padding: '8px 4px' } }}
                        sx={{ width: 64, '& .MuiOutlinedInput-root': { background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                      />
                    </Box>
                  ))}
                </Box>
                {(gineco.gestaciones || gineco.partos || gineco.cesareas || gineco.abortos || gineco.obitos || gineco.ectopicos) ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, mt: 0.5, px: 1.5, py: 0.5, borderRadius: 2, background: '#fce7f3', border: '1px solid #f9a8d4' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#9d174d' }}>Fórmula:</Typography>
                    <Typography sx={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 800, color: '#9d174d', letterSpacing: 1 }}>
                      G{gineco.gestaciones || 0} P{gineco.partos || 0} C{gineco.cesareas || 0} A{gineco.abortos || 0} O{gineco.obitos || 0} E{gineco.ectopicos || 0}
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              {/* Bloque 3: Anticoncepción y preventivo */}
              <Box sx={{ p: 2, borderRadius: 2.5, background: '#fdf2f8', border: '1px solid #fbcfe8' }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9d174d', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
                  Anticoncepción y preventivo
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>Método anticonceptivo</Typography>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select displayEmpty value={gineco.metodo_anticonceptivo || ''} onChange={(e) => setGineco({ ...gineco, metodo_anticonceptivo: e.target.value })}
                      sx={{ borderRadius: 1.5, background: '#fff', fontSize: 12, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } }}>
                      <MenuItem value=""><em>No especificado</em></MenuItem>
                      <MenuItem value="ninguno">Ninguno</MenuItem>
                      <MenuItem value="hormonal_oral">Hormonal oral</MenuItem>
                      <MenuItem value="hormonal_inyectable">Hormonal inyectable</MenuItem>
                      <MenuItem value="implante">Implante subdérmico</MenuItem>
                      <MenuItem value="diu">DIU</MenuItem>
                      <MenuItem value="diu_hormonal">DIU hormonal</MenuItem>
                      <MenuItem value="preservativo">Preservativo</MenuItem>
                      <MenuItem value="otb">OTB (ligadura)</MenuItem>
                      <MenuItem value="vasectomia_pareja">Vasectomía de pareja</MenuItem>
                      <MenuItem value="ritmo">Ritmo / natural</MenuItem>
                      <MenuItem value="otro">Otro</MenuItem>
                    </Select>
                  </FormControl>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>Último Papanicolaou</Typography>
                    <TextField type="date" size="small" value={gineco.ultimo_papanicolaou || ''} onChange={(e) => setGineco({ ...gineco, ultimo_papanicolaou: e.target.value })} InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: 13, padding: '6px 8px' } }}
                      sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                    />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#ec4899', mb: 0.3 }}>Última mastografía</Typography>
                    <TextField type="date" size="small" value={gineco.ultima_mastografia || ''} onChange={(e) => setGineco({ ...gineco, ultima_mastografia: e.target.value })} InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { fontSize: 13, padding: '6px 8px' } }}
                      sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 1.5, background: '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ec4899', borderWidth: 2 } } }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function SeccionHeader({ icono, color, titulo, subtitulo }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Box sx={{ width: 34, height: 34, borderRadius: 2, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${color}40` }}>
        {React.cloneElement(icono, { sx: { fontSize: 18, color: '#fff' } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>{titulo}</Typography>
        {subtitulo && <Typography sx={{ fontSize: 11, color: '#64748b' }}>{subtitulo}</Typography>}
      </Box>
    </Box>
  );
}

function hace(ts) {
  const diff = Date.now() - ts;
  if (diff < 5000) return 'ahora';
  if (diff < 60000) return 'hace unos segundos';
  const min = Math.floor(diff / 60000);
  if (min === 1) return 'hace 1 minuto';
  if (min < 60) return `hace ${min} minutos`;
  return 'hace un momento';
}

function ListaDinamica({ titulo, placeholder, items, onAdd, onRemove, renderItem, colorChip, colorTexto }) {
  const [valor, setValor] = useState('');
  const handleAdd = () => { if (valor.trim()) { onAdd(valor.trim()); setValor(''); } };
  const tienePendiente = valor.trim().length > 0;
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: 12, mb: 0.5 }}>{titulo}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: tienePendiente ? 0.5 : 1 }}>
        <TextField fullWidth size="small" placeholder={placeholder} value={valor} onChange={(e) => setValor(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }} onBlur={handleAdd} sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }} />
        <IconButton onClick={handleAdd} sx={{ background: '#eef2ff', color: '#4f46e5', width: 36, height: 36 }}><AddIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      {tienePendiente && (
        <Typography sx={{ fontSize: 10.5, color: '#92400e', background: '#fef3c7', px: 1, py: 0.25, borderRadius: 1, mb: 1, display: 'inline-block' }}>
          Texto sin agregar — sal del campo o presiona Enter para guardarlo
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {items.map((item, i) => <Chip key={i} label={renderItem(item)} onDelete={() => onRemove(i)} deleteIcon={<DeleteIcon sx={{ fontSize: 14 }} />} size="small" sx={{ background: colorChip || '#eef2ff', color: colorTexto || '#4f46e5', fontWeight: 600, fontSize: 11 }} />)}
      </Box>
    </Box>
  );
}
